/**
 * Staff metrics for the support desk. Customers do not see this page.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { authFetch, authPost } from '../../utils/api';
import './Support.css';

function hours(minutes) {
  if (minutes == null) return '—';
  if (minutes < 90) return `${Math.round(minutes)} min`;
  return `${Math.round(minutes / 60)} h`;
}

export default function SupportInsights() {
  const [history, setHistory] = useState('live');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    authFetch(`/api/tickets/insights?history=${history}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not load insights.');
        return data;
      })
      .then((data) => { if (!cancelled) setReport(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [history]);

  async function draft(clusterKey) {
    setError('');
    const res = await authPost('/api/faq/draft', { clusterKey });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Could not draft that FAQ.');
      return;
    }
    window.location.assign('/support/settings');
  }

  return (
    <section className="support-page">
      <div className="support-head">
        <h1>Support insights</h1>
        <Link className="rf-btn rf-btn-outline" to="/support">Back to tickets</Link>
      </div>
      <div className="support-queues" role="tablist">
        <button type="button" className={`support-queue ${history === 'live' ? 'active' : ''}`} onClick={() => setHistory('live')}>Since go-live</button>
        <button type="button" className={`support-queue ${history === 'zendesk' ? 'active' : ''}`} onClick={() => setHistory('zendesk')}>Including Zendesk history</button>
      </div>
      {error && <p className="support-error">{error}</p>}
      {report && (
        <>
          <div className="support-metrics">
            <p><strong>{report.backlog}</strong> open</p>
            <p><strong>{report.unassigned}</strong> unassigned</p>
            <p><strong>{hours(report.medianFirstReplyMinutes)}</strong> median first reply</p>
            <p><strong>{hours(report.medianResolutionMinutes)}</strong> median resolution</p>
            <p><strong>{report.satisfaction == null ? '—' : `${report.satisfaction}%`}</strong> good ratings{report.satisfactionCount ? ` (${report.satisfactionCount})` : ''}</p>
          </div>
          <details open className="support-fold">
            <summary>Opened and solved, last 12 weeks</summary>
            <div className="support-chart">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={report.weeks || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="created" fill="#a74e9e" />
                  <Bar dataKey="solved" fill="#2f7d4a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </details>
          <details className="support-fold">
            <summary>Load, reopens and one-touch</summary>
            <p>{report.reopenRate}% of tickets were reopened. {report.oneTouchRate}% of solved tickets were one-touch.</p>
            <ul>
              {(report.byPerson || []).map((row) => <li key={row.name}>{row.name}: {row.count} open</li>)}
            </ul>
            <ul>
              {(report.byDiscipline || []).map((row) => <li key={row.name}>{row.name}: {row.count}</li>)}
            </ul>
          </details>
          <details className="support-fold">
            <summary>Top tags and organisations</summary>
            <ul>{(report.topTags || []).map((row) => <li key={row.name}>{row.name}: {row.count}</li>)}</ul>
            <ul>{(report.topOrgs || []).map((row) => <li key={row.name}>{row.name}: {row.count}</li>)}</ul>
          </details>
          <details open className="support-fold">
            <summary>Recurring issues</summary>
            {(report.clusters || []).length === 0 && <p className="support-meta">Nothing has repeated twice yet.</p>}
            <ul>
              {(report.clusters || []).map((row) => (
                <li key={row.key}>
                  {row.component} and {row.symptom}: {row.count} tickets, {row.orgs.join(', ') || 'no organisation'}, last {row.lastAt ? row.lastAt.slice(0, 10) : '—'}
                  {' '}
                  <button type="button" className="rf-btn rf-btn-secondary" onClick={() => draft(row.key)}>Draft FAQ</button>
                </li>
              ))}
            </ul>
          </details>
          <details className="support-fold">
            <summary>Machine age and watch list</summary>
            <ul>
              {(report.patterns || []).map((row) => (
                <li key={row.tag}>
                  {row.tag}: {row.ages.join(' and ')} months, {row.machines} machines{row.small ? ' (small sample)' : ''}
                </li>
              ))}
            </ul>
            <ul>
              {(report.watch || []).map((row) => (
                <li key={`${row.machineId}-${row.tag}`}>{row.client} {row.site}: {row.tag} at {row.ageMonths} months. Others failed at {row.seenAt}.</li>
              ))}
            </ul>
            <p className="support-meta">Send any check email yourself. This page does not email customers.</p>
          </details>
          <details className="support-fold">
            <summary>Upcoming installs</summary>
            <ul>
              {(report.upcoming || []).map((row) => (
                <li key={row.id}>{row.client} · {row.site} · {row.platform} · {row.upcomingOn}</li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
